import type { EmailToCardAddressView } from '@molt/shared';
import { Copy, Mail, RefreshCw } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { Button, Dialog, Notice, TextField } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface CardEmailToCardSectionProps {
  readonly address: EmailToCardAddressView | undefined;
  readonly loading?: boolean | undefined;
  readonly loadError?: string | undefined;
  readonly canRegenerate: boolean;
  readonly disabledReason: string | null;
  readonly regenerateBusy?: boolean | undefined;
  readonly regenerateError?: string | undefined;
  readonly onRegenerate: () => void;
}

export function CardEmailToCardSection({
  address,
  loading = false,
  loadError,
  canRegenerate,
  disabledReason,
  regenerateBusy,
  regenerateError,
  onRegenerate,
}: CardEmailToCardSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const checking = loading;
  const emailAddress = address?.address ?? '';
  const hasAddress = emailAddress !== '';
  const clipboardAvailable =
    typeof navigator !== 'undefined' && navigator.clipboard?.writeText !== undefined;
  const resetDisabledReason = checking
    ? t('cardBack.emailToCard.checking')
    : (loadError ??
      (!canRegenerate
        ? (disabledReason ?? t('cardBack.emailToCard.resetDisabledFallback'))
        : !hasAddress
          ? t('cardBack.emailToCard.addressUnavailable')
          : null));
  const copyDisabledReason = !hasAddress
    ? t('cardBack.emailToCard.addressUnavailable')
    : clipboardAvailable
      ? null
      : t('cardBack.emailToCard.clipboardUnavailable');
  const regenerateBusyProps =
    regenerateBusy === undefined ? {} : { busy: regenerateBusy };

  async function copyAddress(): Promise<void> {
    if (!hasAddress || !clipboardAvailable) return;
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopyError(null);
      setCopyMessage(t('cardBack.emailToCard.announce.copied'));
    } catch {
      setCopyMessage(null);
      setCopyError(t('cardBack.emailToCard.announce.copyFailed'));
    }
  }

  function confirmReset(): void {
    if (regenerateBusy === true) return;
    setCopyMessage(null);
    setCopyError(null);
    onRegenerate();
    setResetConfirmOpen(false);
  }

  return (
    <>
      <section aria-labelledby="email-to-card-heading" className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 id="email-to-card-heading" className="text-sm font-semibold text-ink">
            {t('cardBack.emailToCard.heading')}
          </h3>
          <p className="text-sm text-ink-muted">
            {t('cardBack.emailToCard.description')}
          </p>
        </div>

        {checking ? (
          <Notice tone="info" body={t('cardBack.emailToCard.checking')} />
        ) : loadError !== undefined ? (
          <Notice tone="bad" body={loadError} />
        ) : address === undefined ? (
          <Notice
            tone="locked"
            body={disabledReason ?? t('cardBack.emailToCard.lockedFallback')}
          />
        ) : address.active ? (
          <TextField
            label={t('cardBack.emailToCard.addressLabel')}
            readOnly
            value={emailAddress}
            leadingIcon={<Mail aria-hidden="true" className="size-4" />}
          />
        ) : (
          <Notice tone="warn" body={t('cardBack.emailToCard.inactive')} />
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            aria-label={t('cardBack.emailToCard.copyLabel')}
            disabled={copyDisabledReason !== null}
            disabledReason={copyDisabledReason ?? undefined}
            iconStart={<Copy aria-hidden="true" className="size-4" />}
            onClick={() => {
              void copyAddress();
            }}
          >
            {t('cardBack.emailToCard.copy')}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={resetDisabledReason !== null}
            disabledReason={resetDisabledReason ?? undefined}
            iconStart={<RefreshCw aria-hidden="true" className="size-4" />}
            onClick={() => setResetConfirmOpen(true)}
            {...regenerateBusyProps}
          >
            {t('cardBack.emailToCard.reset')}
          </Button>
        </div>

        {regenerateError !== undefined ? (
          <Notice tone="bad" body={regenerateError} />
        ) : null}
        {copyMessage !== null ? <Notice tone="info" body={copyMessage} /> : null}
        {copyError !== null ? <Notice tone="bad" body={copyError} /> : null}
      </section>

      <Dialog
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        size="sm"
        title={t('cardBack.emailToCard.resetDialog.title')}
        description={t('cardBack.emailToCard.resetDialog.description')}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setResetConfirmOpen(false)}
            >
              {t('cardBack.emailToCard.resetDialog.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={confirmReset}
              {...regenerateBusyProps}
            >
              {t('cardBack.emailToCard.resetDialog.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">
          {t('cardBack.emailToCard.resetDialog.body')}
        </p>
      </Dialog>
    </>
  );
}
